/*
 * Developed by CQ Inversiones SAS.  Copyright (c) 2024. All rights reserved.
 * Desarrollado por CQ Inversiones SAS.  Copyright (c) 2024. Todos los derechos reservados.
 */

import {Injectable} from '@angular/core';
import {MkadBaseService, URLS_SERVICE} from '@shared/services';
import {map, Observable, Subject, takeUntil} from 'rxjs';
import {IconFormInterface, IconListInterface} from '@shared/interface';
import {HttpResponse, HttpStatusCode} from '@angular/common/http';

export type MkadCallback = () => void;

@Injectable({
  providedIn: 'root',
})
export class MkadIconsService extends MkadBaseService {
  readonly icons = URLS_SERVICE.system.icons;
  private _iconList: IconListInterface[] = [];
  private unsubscribe$ = new Subject<void>();

  getList(): Observable<IconListInterface[] | null> {
    return this.http.post<IconListInterface[]| null>(this.baseCore + this.icons.base + this.icons.list);
  }

  list(callback?: MkadCallback): void {
    this.getList()
      .pipe(
        takeUntil(this.unsubscribe$),
      )
      .subscribe({
        next: (data) => {
          if (data && data.length > 0) this._iconList = [...data];
          if (callback) callback();
        },
        error: () => this._iconList = [],
      });
  }

  safeList(callback?: MkadCallback): void {
    if (this._iconList.length === 0) {
      this.list(callback);
    } else {
      if (callback) callback();
    }
  }

  unSubscribe(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  get iconList(): IconListInterface[] {
    return this._iconList;
  }

  create(payload: IconFormInterface): Observable<IconListInterface> {
    return this.http.post<IconListInterface>(this.baseCore + this.icons.base + this.icons.create, payload);
  }

  update(payload: IconFormInterface): Observable<IconFormInterface> {
    return this.http.post<IconFormInterface>(this.baseCore + this.icons.base + this.icons.update, payload);
  }

  get(param: number): Observable<IconFormInterface> {
    return this.http.post<IconFormInterface>(this.baseCore + this.icons.base + this.icons.get, {id: param});
  }

  /**
   * @description
   * Method that delete a specific icon
   * @param param id of the customer
   */
  delete(param: number[]): Observable<boolean> {
    return this.http.post<HttpResponse<boolean>>(this.baseCore + this.icons.base + this.icons.delete, {ids: param}).pipe(
      map((prevResponse) => {
          if (!prevResponse) return true;
          return prevResponse.status === HttpStatusCode.Ok;
        },
      ));
  }
}
